import i18n from 'i18next';
import { push } from 'redux-first-history';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import workspaceService from '@app/libs/workspaceService';
import {
  DOCUMENTS__DOCUMENT_BLOCK_REQUEST,
  DOCUMENTS__DOCUMENT_PROGRESS_REJECT_REQUEST,
  DOCUMENTS__DOCUMENT_UNBLOCK_REQUEST,
  DOCUMENTS__SIGNING_CANCEL_REQUEST,
} from '@app/store/actionTypes';
import {
  blockDocument,
  blockDocumentFailure,
  blockDocumentSuccess,
  cancelSigning,
  cancelSigningFailure,
  cancelSigningSuccess,
  rejectDocument,
  rejectDocumentFailure,
  rejectDocumentSuccess,
  unBlockDocument,
  unBlockDocumentFailure,
  unBlockDocumentSuccess,
} from '@app/store/documents/actions';
import { modalOpen } from '@app/store/modal/actions';
import { postDocumentsParticipantsRejection } from '@app/store/transport-layer/document/documents/participants/rejection';
import { postDocumentsParticipantsSigningCancellation } from '@app/store/transport-layer/document/documents/participants/signing/cancellation';
import { setDocumentAccessable } from '@app/store/transport-layer/document/documents/signer-access';

/**
 * 문서를 차단한다.
 * @params {string} params.documentId - 차단할 문서의 아이디
 */
export function* blockDocumentSaga({ payload }: ReturnType<typeof blockDocument>) {
  const { documentId, invalidate } = payload;
  try {
    yield call(setDocumentAccessable, {
      workspaceId: workspaceService.getWorkspaceId(),
      documentId,
      isAccessable: false,
    });
    yield put(blockDocumentSuccess());
    yield put(
      modalOpen('CONFIRM', {
        title: '차단 완료',
        content: '문서 확인이 차단되어\n모든 서명자들은 문서를 볼 수 없습니다.',
      })
    );

    if (invalidate !== undefined) {
      yield call(invalidate);
    }
  } catch (error) {
    yield put(blockDocumentFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '차단 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

/**
 * 문서 서명을 취소한다.
 * @params {object}
 * @params {string} params.documentId
 * @params {string} params.participantId
 * @params {string} params.message
 * @params {bool} params.isDocumentBlock
 */
export function* documentsCancelSigning({
  payload: { documentId, isDocumentBlock, message, participantId },
}: ReturnType<typeof cancelSigning>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsParticipantsSigningCancellation, {
      documentId,
      message,
      participantId,
      workspaceId,
    });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, {
        workspaceId: workspaceService.getWorkspaceId(),
        documentId,
        isAccessable: false,
      });
    }
    yield put(cancelSigningSuccess());
    const modalMessage = isDocumentBlock
      ? i18n.t('cancelDocument-cancelMessageWithBlock')
      : i18n.t('cancelDocument-cancelMessageWithoutBlock');
    yield put(
      modalOpen('CONFIRM', {
        title: i18n.t('cancelDocument-cancelTitle'),
        content: modalMessage,
        closeText: i18n.t('Global-confirm'),
      })
    );

    yield put(push('/documents'));
  } catch (error) {
    yield put(cancelSigningFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '서명 취소 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

/**
 * 문서 진행을 거절한다.
 * @params {object}
 * @params {string} params.documentId
 * @params {string} params.participantId
 * @params {string} params.message
 * @params {bool} params.isDocumentBlock
 */
export function* rejectDocumentSaga({
  payload: { documentId, participantId, message, isDocumentBlock },
}: ReturnType<typeof rejectDocument>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsParticipantsRejection, {
      documentId,
      message,
      participantId,
      workspaceId,
    });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, {
        workspaceId: workspaceService.getWorkspaceId(),
        documentId,
        isAccessable: false,
      });
    }
    yield put(rejectDocumentSuccess());
    const modalMessage = isDocumentBlock
      ? i18n.t('rejectDocument-rejectMessageWithBlock')
      : i18n.t('rejectDocument-rejectMessageWithoutBlock');
    yield put(
      modalOpen('CONFIRM', {
        title: i18n.t('rejectDocument-rejectTitle'),
        content: modalMessage,
        closeText: i18n.t('Global-confirm'),
      })
    );

    yield put(push('/documents'));
  } catch (error) {
    yield put(rejectDocumentFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '거절 실패',
        content: '거절이 실패되었습니다.\n다시 시도해 주세요.',
      })
    );
  }
}

/**
 * 문서차단을 해제한다.
 * @params {string} params.documentId - 차단할 문서의 아이디
 */
export function* unBlockDocumentSaga({ payload }: ReturnType<typeof unBlockDocument>) {
  const { documentId } = payload;
  try {
    yield call(setDocumentAccessable, {
      workspaceId: workspaceService.getWorkspaceId(),
      documentId,
      isAccessable: true,
    });
    yield put(unBlockDocumentSuccess());
    yield put(
      modalOpen('CONFIRM', {
        title: '차단 해제 완료',
        content: '차단 해제가 완료되었습니다.',
      })
    );
    if (payload.invalidate !== undefined) {
      yield call(payload.invalidate);
    }
  } catch (error) {
    yield put(unBlockDocumentFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '차단 해제 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export default function* sagas() {
  yield all([
    takeEvery(DOCUMENTS__DOCUMENT_BLOCK_REQUEST, blockDocumentSaga),
    takeEvery(DOCUMENTS__SIGNING_CANCEL_REQUEST, documentsCancelSigning),
    takeEvery(DOCUMENTS__DOCUMENT_PROGRESS_REJECT_REQUEST, rejectDocumentSaga),
    takeEvery(DOCUMENTS__DOCUMENT_UNBLOCK_REQUEST, unBlockDocumentSaga),
  ]);
}
